import { useState, useCallback } from 'react';
import { Editor, Transforms, Element, Path } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import {
  ContactCardData,
  ContactCardElement,
  contactCardStore,
  insertContactCard,
  generateVcfString,
  removeContactCard
} from '../plugins/with-contact-cards';

interface UseContactCardReturn {
  showContactCardDialog: boolean;
  setShowContactCardDialog: (show: boolean) => void;
  selectedContactCard: ContactCardElement | null;
  setSelectedContactCard: (card: ContactCardElement | null) => void;
  handleContactCardCreate: (data: Omit<ContactCardData, 'id' | 'vcfString'>) => void;
  handleContactCardEdit: (data: ContactCardData) => void;
  handleContactCardDelete: (id: string) => void;
  handleEditTrigger: (element: ContactCardElement) => void;
  getContactCards: () => ContactCardData[];
  handleContactCardInsert: (contact: ContactCardData) => void;
}

/**
 * Finds the contact card node in the editor.
 * Since only one contact card node is allowed at a time, we can
 * simply query the editor for it when needed instead of storing
 * its path in a ref. This simplifies the code and reduces state management overhead.
 *
 * @param editor - The Slate editor instance
 * @returns A tuple of [node, path] if found, null otherwise
 */
export function getContactCardNode(editor: Editor): [ContactCardElement, Path] | null {
  const [nodeEntry] = Editor.nodes(editor, {
    at: [],
    match: (n): n is ContactCardElement => Element.isElement(n) && n.type === 'contact-card'
  });
  return nodeEntry as [ContactCardElement, Path] | null;
}

/**
 * Hook for managing contact card operations in the SMS editor.
 *
 * Key features:
 * - Dialog state management for creating/editing cards
 * - CRUD operations for contact cards with proper Slate transforms
 * - Simple querying for contact card node (since only one is allowed)
 * - In-memory storage of contact cards (temporary solution)
 * - vCard generation using vcards-js
 *
 * Design decisions:
 * - Query-based approach since only one contact card is allowed
 * - Strong typing for phone/email data to ensure type safety and clarity
 * - Separation of concerns: storage, transforms, and UI state
 * - vCard generation at creation/edit time for immediate validation
 */
export function useContactCard(editor: Editor): UseContactCardReturn {
  const [showContactCardDialog, setShowContactCardDialog] = useState(false);
  const [selectedContactCard, setSelectedContactCard] = useState<ContactCardElement | null>(null);

  // Reset all state related to contact card editing
  const resetState = useCallback(() => {
    setSelectedContactCard(null);
    setShowContactCardDialog(false);
  }, []);

  // Handle edit trigger from clicking a contact card
  const handleEditTrigger = useCallback((element: ContactCardElement) => {
    setSelectedContactCard(element);
    setShowContactCardDialog(true);
  }, []);

  // Create a new contact card template AND insert into editor
  const handleContactCardCreate = useCallback(
    (data: Omit<ContactCardData, 'id' | 'vcfString'>) => {
      const id = uuidv4();
      const vcfString = generateVcfString(data);
      const contactData: ContactCardData = { ...data, id, vcfString };

      // Store as available template
      contactCardStore.set(id, contactData);

      // Insert into editor
      insertContactCard(editor, contactData);
      resetState();
    },
    [editor, resetState]
  );

  // Edit an existing card in the editor
  const handleContactCardEdit = useCallback(
    (data: ContactCardData) => {
      try {
        // Find the current contact card node
        const [node, path] =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          Editor.nodes(editor, {
            at: [],
            match: (n) => Element.isElement(n) && n.type === 'contact-card' && n.data.id === data.id
          }).next().value ?? [];

        if (!node || !path) {
          console.error('No contact card node found');
          resetState();
          return;
        }

        // Update vCard string
        const vcfString = generateVcfString(data);
        const updatedData = { ...data, vcfString };

        // Update both the editor node and the template store
        Transforms.setNodes<ContactCardElement>(editor, { data: updatedData }, { at: path });
        contactCardStore.set(data.id, updatedData);

        resetState();
      } catch (error) {
        console.error('Failed to update contact card:', error);
        resetState();
      }
    },
    [editor, resetState]
  );

  // Delete a card from the editor only
  const handleContactCardDelete = useCallback(
    (id: string) => {
      const [node] =
        Editor.nodes(editor, {
          at: [],
          match: (n) => Element.isElement(n) && n.type === 'contact-card' && n.data.id === id
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        }).next().value || [];

      if (node) {
        removeContactCard(editor, node as ContactCardElement);
      }
      resetState();
    },
    [editor, resetState]
  );

  // Get all available contact card templates
  const getContactCards = useCallback(() => {
    return Array.from(contactCardStore.values());
  }, []);

  const handleContactCardInsert = useCallback(
    (contact: ContactCardData) => {
      insertContactCard(editor, contact);
    },
    [editor]
  );

  return {
    showContactCardDialog,
    setShowContactCardDialog,
    selectedContactCard,
    setSelectedContactCard,
    handleContactCardCreate,
    handleContactCardEdit,
    handleContactCardDelete,
    handleEditTrigger,
    getContactCards,
    handleContactCardInsert
  };
}
